/**
 * V2 Compatibility Utilities
 * 
 * Contains mapping functions from V2 that are still needed for V3
 * These functions transform JobDescriptionEntity to form data format
 */

import dayjs from 'dayjs';
import { JobDescriptionEntity, Requirements } from '../../../Entities/JobDescription';
import { AdditionalRequirementDto } from '../../../Dto/RequirementCategory.dto';
import { InterviewRoundType } from '../../../Dto/GenerateJobDescription.dto';

/**
 * Step 1: Basic Details
 */
export interface Step1Data {
  title: string | null;
  position: string | null;
  description: string | null;
  numberOfOpenings: number | null;
  noticePeriod: number | null;
  ctc: string | null; // Format: "VALUE CURRENCY" (e.g., "260000 INR")
  endDate: string | null;
}

/**
 * Transform Step1Data to form values
 */
export const transformStep1ToFormValues = (data: Step1Data | null, defaultCurrency?: string): any => {
  if (!data) {
    return {
      noticePeriod: '0',
      currencyCode: defaultCurrency || 'INR',
    };
  }
  
  let ctcValue: number | null = null;
  let parsedCurrency: string = defaultCurrency || 'INR';
  
  if (data.ctc) {
    if (typeof data.ctc === 'string') {
      const parts = data.ctc.trim().split(/\s+/);
      if (parts.length >= 2) {
        const numericPart = parts[0];
        const currencyPart = parts.slice(1).join(' ').toUpperCase();
        const numericValue = Number(numericPart);
        if (!isNaN(numericValue) && numericValue > 0) {
          ctcValue = numericValue;
          parsedCurrency = currencyPart;
        }
      } else {
        const numericValue = Number(data.ctc);
        if (!isNaN(numericValue) && numericValue > 0) {
          ctcValue = numericValue;
        }
      }
    } else if (typeof data.ctc === 'number') {
      ctcValue = data.ctc > 0 ? data.ctc : null;
    }
  }
  
  return {
    title: data.title || '',
    position: data.position || '',
    description: data.description || '',
    numberOfOpenings: data.numberOfOpenings || 1,
    noticePeriod: data.noticePeriod !== null && data.noticePeriod !== undefined 
      ? String(data.noticePeriod) 
      : '0',
    ctc: ctcValue,
    currency: parsedCurrency || undefined,
    endDate: data.endDate ? dayjs(data.endDate) : null,
    currencyCode: parsedCurrency || defaultCurrency || 'INR',
  };
};

/**
 * Map JobDescriptionEntity to Step1Data
 * Handles both new format (ctc as number + currency as separate field) and old format (ctc as "VALUE CURRENCY" string)
 */
export const mapJobDescriptionToStep1 = (jobPost: JobDescriptionEntity): Step1Data => {
  let formattedCTC: string | null = null;
  let currencyFromBackend = (jobPost as any).currency;
  
  if (jobPost.ctc) {
    if (typeof jobPost.ctc === 'number') {
      // New format: CTC is number, currency is separate field
      const currency = currencyFromBackend || 'INR';
      formattedCTC = `${jobPost.ctc} ${currency}`;
    } else if (typeof jobPost.ctc === 'string') {
      // Old format: CTC is "VALUE CURRENCY" string
      formattedCTC = jobPost.ctc;
    } else {
      // Fallback: try to convert to number
      const numericValue = Number(jobPost.ctc);
      if (!isNaN(numericValue) && numericValue > 0) {
        const currency = currencyFromBackend || 'INR';
        formattedCTC = `${numericValue} ${currency}`;
      }
    }
  }
  
  return {
    title: jobPost.title || null,
    position: jobPost.position || null,
    description: jobPost.description || null,
    numberOfOpenings: jobPost.numberOfOpenings || null,
    noticePeriod: jobPost.noticePeriod || null,
    ctc: formattedCTC,
    endDate: jobPost.endDate || null,
  };
};

/**
 * Step 2: Requirements
 */
export interface Step2Data {
  requirements: Requirements[];
  additionalRequirements: AdditionalRequirementDto[];
  history?: Array<{
    id: string;
    timestamp: number;
    prompt: string;
    requirementsBefore: Requirements[];
    requirementsAfter: Requirements[];
    action: 'refine' | 'generate';
  }>;
}

/**
 * Map JobDescriptionEntity to Step2Data
 */
export const mapJobDescriptionToStep2 = (jobPost: JobDescriptionEntity | null): Step2Data => {
  if (!jobPost) {
    return {
      requirements: [],
      additionalRequirements: [],
      history: [], // Initialize empty history array for edit/clone modes
    };
  }

  const requirements: Requirements[] = (jobPost.requirements || []).map((req, index) => ({
    id: req.id || index + 1,
    topic: '',
    description: req.description || req.topic || '',
    mustMatch: req.mustMatch ?? true,
  }));

  // Filter out manual_questions from additionalRequirements - they belong to Step4 (manualScreeningQuestions)
  // manual_questions are handled separately in Step4 and should not be in Step2's additionalRequirements
  const additionalRequirements = (jobPost.additionalRequirements || []).filter(
    (req) => req.topic !== 'manual_questions'
  );

  return {
    requirements,
    additionalRequirements,
    history: [], // Initialize empty history array for edit/clone modes
  };
};

/**
 * Step 3: Interview Rounds
 */
export interface Step3Data {
  interviewRounds: InterviewRoundType[];
}

/**
 * Map JobDescriptionEntity to Step3Data
 */
export const mapJobDescriptionToStep3 = (jobPost: JobDescriptionEntity | null): Step3Data => {
  if (!jobPost?.interviewRounds) {
    return {
      interviewRounds: [],
    };
  }

  // Filter out Job Alignment from Step 3 (Job Alignment is now in Step 4)
  // Note: Database stores 0-indexed round numbers, form uses 1-indexed
  // We add +1 to convert from database format to form format
  const interviewRounds: InterviewRoundType[] = (jobPost.interviewRounds || [])
    .filter((round) => {
      const id = round.interviewRoundId || '';
      return id !== 'job-alignment';
    })
    .map((round) => ({
      interviewRoundId: round.interviewRoundId || null,
      interviewRoundNumber: (round.interviewRoundNumber ?? 0) + 1, // Convert 0-indexed (DB) to 1-indexed (form)
      isMustQualify: round.isMustQualify ?? true,
      passPercentage: round.passPercentage, // Include pass percentage if available
      isAIGenerated: round.isAIGenerated ?? true, // Include isAIGenerated
    }));

  return {
    interviewRounds,
  };
};

/**
 * Step 4: Manual Screening Questions
 * Note: This is legacy interface for v2Compatibility - actual Step4Data is defined in schemas/jobPostValidation.ts
 */
export interface ManualScreeningQuestion {
  id: number;
  description: string;
  order: number;
}

export interface Step4Data {
  manualScreeningQuestions: ManualScreeningQuestion[];
}

/**
 * Map JobDescriptionEntity to Step4Data
 * Note: This is legacy - actual mapping is done in Step4HRScreening/utils/formTransformers.ts
 */
export const mapJobDescriptionToStep4 = (jobPost: JobDescriptionEntity): Step4Data | null => {
  if (!jobPost.additionalRequirements || jobPost.additionalRequirements.length === 0) {
    return {
      manualScreeningQuestions: [],
    };
  }

  // Filter only manual_questions (legacy mapping - actual implementation filters in Step4HRScreening/utils/formTransformers.ts)
  const manualScreeningQuestions: ManualScreeningQuestion[] = jobPost.additionalRequirements
    .filter((req) => req.topic === 'manual_questions')
    .map((req, index) => ({
      id: index + 1,
      description: req.description || req.topic || '',
      order: index + 1,
    }));

  return {
    manualScreeningQuestions,
  };
};

/**
 * Step 5: Post Assessment
 */
export interface Step5Data {
  onlineInterview: boolean;
  interviewers: any[];
  interviewGuests: string[];
}

/**
 * Map JobDescriptionEntity to Step5Data
 */
export const mapJobDescriptionToStep5 = (jobPost: JobDescriptionEntity): Step5Data | null => {
  return {
    onlineInterview: jobPost.onlineInterview ?? false,
    interviewers: jobPost.interviewers || [],
    interviewGuests: jobPost.interviewGuests || [],
  };
};

/**
 * Transform JobDescriptionEntity from API to store format
 */
export const transformJobPostToStoreData = (jobPost: JobDescriptionEntity) => {
  return {
    step1: mapJobDescriptionToStep1(jobPost) as Step1Data,
    step2: mapJobDescriptionToStep2(jobPost) as Step2Data,
    step3: mapJobDescriptionToStep3(jobPost) as Step3Data,
    step4: mapJobDescriptionToStep4(jobPost) as Step4Data | null,
    step5: mapJobDescriptionToStep5(jobPost) as Step5Data | null,
  };
};

